import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@apollo/client";
import {
  Card, Typography, Input, Button, Modal, Form, Avatar, Tag, Row, Col,
  Select, Tooltip, Popconfirm, Empty, Spin, Badge, Divider, message, Switch,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, UserAddOutlined, UserDeleteOutlined,
  MailOutlined, SearchOutlined, EditOutlined, TeamOutlined, CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  GET_ALL_SALES_EMAILS,
  CREATE_SALES_EMAIL,
  UPDATE_SALES_EMAIL,
  DELETE_SALES_EMAIL,
  ASSIGN_SALES_EMAIL,
  REVOKE_SALES_EMAIL,
} from "../../../Queries/SalesEmails";

const { Title, Text } = Typography;

const filterActive = (emps) =>
  emps?.filter((e) => e.employeestatus === "Active") || [];

export default function SalesOutlookAccess() {
  const allEmployees = useSelector((s) => s.employee.AllEmployees);
  const employees = useMemo(() => filterActive(allEmployees), [allEmployees]);

  const [searchTerm, setSearchTerm] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(null);       // { id, email, name }
  const [assignModal, setAssignModal] = useState(null);   // salesEmail object
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const { data, loading, refetch } = useQuery(GET_ALL_SALES_EMAILS, { fetchPolicy: "network-only" });
  const salesEmails = data?.getAllSalesEmails || [];

  const [createEmail, { loading: creating }] = useMutation(CREATE_SALES_EMAIL);
  const [updateEmail, { loading: updating }] = useMutation(UPDATE_SALES_EMAIL);
  const [deleteEmail] = useMutation(DELETE_SALES_EMAIL);
  const [assignEmail, { loading: assigning }] = useMutation(ASSIGN_SALES_EMAIL);
  const [revokeEmail] = useMutation(REVOKE_SALES_EMAIL);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? salesEmails.filter(
          (s) => s.email?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q)
        )
      : salesEmails;
  }, [salesEmails, searchTerm]);

  // Employees not yet assigned to this sales email
  const unassignedEmployees = useMemo(() => {
    if (!assignModal) return employees;
    const assignedIds = new Set(assignModal.assignedTo?.map((e) => e._id));
    return employees.filter((e) => !assignedIds.has(e.id));
  }, [assignModal, employees]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const { data: res } = await createEmail({ variables: { email: values.email, name: values.name || "" } });
      if (res?.createSalesEmail?.success) {
        message.success("Sales email added");
        form.resetFields();
        setAddModalOpen(false);
        refetch();
      } else {
        message.error(res?.createSalesEmail?.message || "Failed");
      }
    } catch {}
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const { data: res } = await updateEmail({ variables: { id: editModal.id, name: values.name } });
      if (res?.updateSalesEmail?.success) {
        message.success("Updated");
        setEditModal(null);
        refetch();
      } else {
        message.error(res?.updateSalesEmail?.message || "Failed");
      }
    } catch {}
  };

  const handleDelete = async (id) => {
    const { data: res } = await deleteEmail({ variables: { id } });
    if (res?.deleteSalesEmail?.success) {
      message.success("Deleted");
      refetch();
    } else {
      message.error(res?.deleteSalesEmail?.message || "Failed");
    }
  };

  const handleToggleStatus = async (se) => {
    await updateEmail({ variables: { id: se._id, status: !se.status } });
    refetch();
  };

  const handleAssign = async (employeeId) => {
    const { data: res } = await assignEmail({
      variables: { salesEmailId: assignModal._id, employeeId },
    });
    if (res?.assignSalesEmailToEmployee?.success) {
      message.success("Assigned");
      refetch();
      // refresh modal data
      setAssignModal((prev) => {
        const emp = employees.find((e) => e.id === employeeId);
        if (!emp) return prev;
        return {
          ...prev,
          assignedTo: [...(prev.assignedTo || []), { _id: emp.id, firstname: emp.firstname, lastname: emp.lastname, email: emp.email, department: emp.department }],
        };
      });
    } else {
      message.error(res?.assignSalesEmailToEmployee?.message || "Failed");
    }
  };

  const handleRevoke = async (salesEmailId, employeeId) => {
    const { data: res } = await revokeEmail({ variables: { salesEmailId, employeeId } });
    if (res?.revokeSalesEmailFromEmployee?.success) {
      message.success("Access revoked");
      refetch();
      setAssignModal((prev) =>
        prev ? { ...prev, assignedTo: prev.assignedTo.filter((e) => e._id !== employeeId) } : prev
      );
    } else {
      message.error("Failed to revoke");
    }
  };

  const stats = useMemo(() => ({
    total: salesEmails.length,
    active: salesEmails.filter((s) => s.status).length,
    totalAssignments: salesEmails.reduce((a, s) => a + (s.assignedTo?.length || 0), 0),
  }), [salesEmails]);

  return (
    <div style={{ padding: "24px", background: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10,
            background: "linear-gradient(135deg, #11998e, #38ef7d)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <MailOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Sales Outlook</Title>
            <Text type="secondary">Manage sales email accounts and assign them to your team</Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
          style={{ borderRadius: 8, background: "linear-gradient(135deg, #11998e, #38ef7d)", border: "none" }}
        >
          Add Sales Email
        </Button>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          { label: "Total Emails", value: stats.total, color: "#1677ff" },
          { label: "Active", value: stats.active, color: "#52c41a" },
          { label: "Inactive", value: stats.total - stats.active, color: "#ff4d4f" },
          { label: "Total Assignments", value: stats.totalAssignments, color: "#722ed1" },
        ].map((s) => (
          <Col xs={12} sm={6} key={s.label}>
            <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{s.label}</Text>
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Search */}
      <Input
        prefix={<SearchOutlined style={{ color: "#bbb" }} />}
        placeholder="Search by email or label..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 20, borderRadius: 10, maxWidth: 400 }}
        allowClear
      />

      {/* Email cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
      ) : filtered.length === 0 ? (
        <Empty description="No sales emails yet. Click 'Add Sales Email' to get started." />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((se) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={se._id}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 14,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  border: `1.5px solid ${se.status ? "#b7eb8f" : "#ffd6d6"}`,
                  background: se.status ? "#fff" : "#fffafa",
                  transition: "all 0.2s",
                  height: "100%",
                }}
                bodyStyle={{ padding: "16px" }}
              >
                {/* Email header */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: se.status
                          ? "linear-gradient(135deg, #11998e, #38ef7d)"
                          : "linear-gradient(135deg, #d9d9d9, #bfbfbf)",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <MailOutlined style={{ color: "#fff", fontSize: 14 }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {se.name || se.email}
                        </Text>
                        {se.name && (
                          <Text type="secondary" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                            {se.email}
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                    <Tooltip title="Edit label">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => { setEditModal({ id: se._id, email: se.email, name: se.name }); editForm.setFieldsValue({ name: se.name }); }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Delete this sales email?"
                      description="This will remove all assignments."
                      onConfirm={() => handleDelete(se._id)}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </div>

                {/* Status toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <Tag color={se.status ? "success" : "error"} icon={se.status ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {se.status ? "Active" : "Inactive"}
                  </Tag>
                  <Switch
                    checked={se.status}
                    onChange={() => handleToggleStatus(se)}
                    size="small"
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                </div>

                <Divider style={{ margin: "10px 0" }} />

                {/* Assigned employees */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                      <TeamOutlined style={{ marginRight: 4 }} />
                      Assigned ({se.assignedTo?.length || 0})
                    </Text>
                  </div>
                  {se.assignedTo?.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {se.assignedTo.slice(0, 4).map((emp) => (
                        <Tooltip key={emp._id} title={`${emp.firstname} ${emp.lastname} — ${emp.department || ""}`}>
                          <Avatar
                            size={28}
                            style={{ background: "linear-gradient(135deg, #667eea, #764ba2)", fontSize: 11, cursor: "pointer" }}
                          >
                            {emp.firstname?.[0]}{emp.lastname?.[0]}
                          </Avatar>
                        </Tooltip>
                      ))}
                      {se.assignedTo.length > 4 && (
                        <Avatar size={28} style={{ background: "#d9d9d9", fontSize: 11 }}>
                          +{se.assignedTo.length - 4}
                        </Avatar>
                      )}
                    </div>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>No one assigned yet</Text>
                  )}
                </div>

                <Button
                  block
                  icon={<UserAddOutlined />}
                  onClick={() => setAssignModal(se)}
                  style={{ borderRadius: 8, fontSize: 12, marginTop: 4 }}
                >
                  Manage Access
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add Email Modal */}
      <Modal
        title={<><PlusOutlined style={{ marginRight: 8 }} />Add Sales Email</>}
        open={addModalOpen}
        onOk={handleCreate}
        onCancel={() => { setAddModalOpen(false); form.resetFields(); }}
        confirmLoading={creating}
        okText="Add Email"
        okButtonProps={{ style: { background: "linear-gradient(135deg, #11998e, #38ef7d)", border: "none" } }}
        centered
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: "email", message: "Enter a valid email" }]}>
            <Input prefix={<MailOutlined />} placeholder="e.g. john.sales@company.com" />
          </Form.Item>
          <Form.Item name="name" label="Display Label">
            <Input placeholder="e.g. John - Cold Outreach (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Label Modal */}
      <Modal
        title={<><EditOutlined style={{ marginRight: 8 }} />Edit Label</>}
        open={!!editModal}
        onOk={handleEdit}
        onCancel={() => setEditModal(null)}
        confirmLoading={updating}
        okText="Save"
        centered
      >
        {editModal && (
          <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Email">
              <Input value={editModal.email} disabled />
            </Form.Item>
            <Form.Item name="name" label="Display Label">
              <Input placeholder="e.g. John - Cold Outreach" />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Assign/Manage Access Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TeamOutlined style={{ color: "#11998e" }} />
            <span>Manage Access — {assignModal?.name || assignModal?.email}</span>
          </div>
        }
        open={!!assignModal}
        onCancel={() => setAssignModal(null)}
        footer={null}
        width={600}
        centered
      >
        {assignModal && (
          <div>
            {/* Currently assigned */}
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Currently Assigned ({assignModal.assignedTo?.length || 0})
            </Text>
            {assignModal.assignedTo?.length === 0 ? (
              <Text type="secondary" style={{ fontSize: 13 }}>No employees assigned yet.</Text>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {assignModal.assignedTo.map((emp) => (
                  <div
                    key={emp._id}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", borderRadius: 8, background: "#f6ffed",
                      border: "1px solid #b7eb8f",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Avatar size={28} style={{ background: "linear-gradient(135deg, #52c41a, #389e0d)", fontSize: 11 }}>
                        {emp.firstname?.[0]}{emp.lastname?.[0]}
                      </Avatar>
                      <div>
                        <Text strong style={{ fontSize: 13 }}>{emp.firstname} {emp.lastname}</Text>
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>{emp.department}</Text>
                      </div>
                    </div>
                    <Popconfirm
                      title="Revoke access?"
                      onConfirm={() => handleRevoke(assignModal._id, emp._id)}
                      okText="Revoke"
                      okButtonProps={{ danger: true }}
                    >
                      <Button type="text" size="small" danger icon={<UserDeleteOutlined />}>Revoke</Button>
                    </Popconfirm>
                  </div>
                ))}
              </div>
            )}

            <Divider />

            {/* Assign new */}
            <Text strong style={{ display: "block", marginBottom: 8 }}>Assign to Employee</Text>
            {unassignedEmployees.length === 0 ? (
              <Text type="secondary">All active employees are already assigned.</Text>
            ) : (
              <Select
                showSearch
                placeholder="Search and select employee..."
                style={{ width: "100%" }}
                loading={assigning}
                filterOption={(input, opt) =>
                  opt?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={unassignedEmployees.map((e) => ({
                  value: e.id,
                  label: `${e.firstname} ${e.lastname} — ${e.department || ""}`,
                }))}
                onSelect={handleAssign}
                value={null}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
